import { jwtDecode } from 'jwt-decode';
import keycloak from '../keycloak';
import SvgColor from '../components/svg-color/SvgColor';
import logger from './logger';

/**
 * Extract client IP from response headers
 */
const extractIpFromHeaders = (headers) => {
  const ipHeaders = [
    'x-forwarded-for',
    'x-real-ip',
    'cf-connecting-ip', // Cloudflare
    'x-client-ip',
    'x-forwarded',
    'forwarded-for',
    'forwarded',
  ];

  for (const header of ipHeaders) {
    const headerValue = headers.get?.(header) || headers[header];
    if (headerValue) {
      // X-Forwarded-For can contain multiple IPs, take the first one
      const ip = headerValue.split(',')[0].trim();
      if (ip && ip !== 'unknown') {
        return ip;
      }
    }
  }
  return null;
};

// Global fetch interceptor to handle 401 errors
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Enhanced fetch with automatic token refresh on 401
export const fetchWithAuth = async (url, options = {}) => {
  const originalRequest = options;

  // Add authorization header if token exists
  const token = getToken();
  if (token) {
    options.headers = {
      ...options.headers,
      Authorization: `Bearer ${token}`,
    };
  }

  try {
    const response = await fetch(url, options);

    // Extract IP from response headers if available
    if (response.headers) {
      const clientIp = extractIpFromHeaders(response.headers);
      // setClientIp will validate and only set if valid (not empty, not 'unknown')
      logger.setClientIp(clientIp);
    }

    // If response is ok, return it
    if (response.ok) {
      return response;
    }

    // If 401 and we have a refresh token, try to refresh
    if (response.status === 401 && getRefreshToken() && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers = {
              ...originalRequest.headers,
              Authorization: `Bearer ${token}`,
            };
            return fetch(url, originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshResponse = await fetch(`${globalThis.REACT_APP_API_PURE}user/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            refresh_token: getRefreshToken(),
          }),
        });

        if (refreshResponse.ok) {
          // Extract IP from refresh response headers if available
          if (refreshResponse.headers) {
            const clientIp = extractIpFromHeaders(refreshResponse.headers);
            // setClientIp will validate and only set if valid (not empty, not 'unknown')
            logger.setClientIp(clientIp);
          }

          const data = await refreshResponse.json();
          setRefreshUserSession(data.access_token, data.refresh_token, data.expires_in);

          processQueue(null, data.access_token);

          // Retry original request with new token
          originalRequest.headers = {
            ...originalRequest.headers,
            Authorization: `Bearer ${data.access_token}`,
          };
          return fetch(url, originalRequest);
        } else {
          // Refresh failed, logout user
          processQueue(new Error('Token refresh failed'), null);
          removeUserSession();
          globalThis.location.href = '/login';
          return Promise.reject(new Error('Session expired'));
        }
      } catch (error) {
        processQueue(error, null);
        removeUserSession();
        globalThis.location.href = '/login';
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    // For other errors, return the response as is
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

// return the user data from the session storage
export const getUser = () => {
  const userStr = sessionStorage.getItem('user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (error) {
      logger.error('Error parsing user data:', error);
      return null;
    }
  }
  return null;
};
export const getFullname = () => sessionStorage.getItem('fullname');
// return the token from the session storage
export const getToken = () => sessionStorage.getItem('token') || null;
export const getRefreshToken = () => sessionStorage.getItem('refreshToken') || null;
export const getAcl = () => sessionStorage.getItem('acl') || null;
export const getAclMenu = (namaMenu) => {
  const jsonString = sessionStorage?.getItem('acl') || '[]';
  try {
    const jsonArray = JSON.parse(jsonString);
    const filteredArray = jsonArray?.filter((item) => item.nama_menu === namaMenu);
    return filteredArray[0] || [];
  } catch (error) {
    logger.error('Error parsing ACL data:', error);
    return [];
  }
};
export const getRole = () => {
  const { token } = keycloak;
  let roles = null;

  if (token && typeof token === 'string') {
    const decoded = jwtDecode(token);
    roles = decoded?.resource_access?.smartshield?.roles?.[0];
  }

  return roles;
};
export const getExpired = () => {
  const expiredRaw = sessionStorage.getItem('expired');
  if (expiredRaw === null) return null;
  const expired = Number(expiredRaw);
  if (Number.isNaN(expired)) return null;
  return expired - 1000;
};

export const fetchLog = async (fakeDataUrl, data = {}) => {
  const postData = {
    refresh_token: keycloak.refreshToken,
    ...data,
  };
  try {
    const response = await fetch(fakeDataUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${keycloak.token}`,
      },
      body: JSON.stringify(postData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    logger.error('Error fetching data:', error);
    throw error;
  }
};
// remove the token and user from the session storage
export const removeUserSession = () => {
  sessionStorage.clear();
  const DB_NAME = 'DialogPreviewCache';
  indexedDB.deleteDatabase(DB_NAME);

  // Call Keycloak logout and redirect to login page
  if (keycloak && keycloak.idToken) {
    fetch(
      `${keycloak.authServerUrl}/realms/${keycloak.realm}/protocol/openid-connect/logout?id_token_hint=${keycloak.idToken}&post_logout_redirect_uri=${globalThis.location.origin}/login`
    )
      .then(async () => {
        // Redirect to login page after successful logout
        globalThis.location.href = '/login';
      })
      .catch((error) => {
        logger.error('Logout error:', error);
        // Even if logout fails, redirect to login page
        globalThis.location.href = '/login';
      });
  } else {
    // If no Keycloak token, just redirect to login
    globalThis.location.href = '/login';
  }
};

// set the token and user from the session storage
export const setUserSession = (token, refshtoken, user, role, fullname) => {
  // Clear permission cache when user session changes
  clearPermissionCache();

  sessionStorage.setItem('refreshToken', refshtoken);
  sessionStorage.setItem('token', token);
  sessionStorage.setItem('user', JSON.stringify(user));
  sessionStorage.setItem('role', role);
  sessionStorage.setItem('fullname', fullname);

  // Fetch fresh permissions for new session
  getMenuAccess(true); // Force refresh
};
export const setRefreshUserSession = (accesToken, refreshToken, expired) => {
  sessionStorage.setItem('expired', Math.floor(Date.now() / 1000) + expired);
  sessionStorage.setItem('token', accesToken);
  sessionStorage.setItem('refreshToken', refreshToken);

  // Clear permission cache when tokens are refreshed to ensure fresh permissions
  clearPermissionCache();
};

// Cache for permission data to avoid frequent API calls
let permissionCache = {
  data: null,
  timestamp: 0,
  ttl: 5 * 60 * 1000, // 5 minutes cache
};

export const getMenuAccess = (forceRefresh = false) => {
  const now = Date.now();

  // Return cached data if still valid and not forcing refresh
  if (!forceRefresh && permissionCache.data && now - permissionCache.timestamp < permissionCache.ttl) {
    return Promise.resolve(permissionCache.data);
  }

  const token = getToken();
  if (!token) {
    logger.warn('No token available for permission check');
    return Promise.reject(new Error('No token available'));
  }

  return fetchWithAuth(`${globalThis.REACT_APP_API_PURE}user/permissions`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  })
    .then((res) => {
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      return res.json();
    })
    .then((data) => {
      const outputArray = data?.map((item) => ({
        nama_menu: item.resourceName,
        create: item?.scopes?.includes('create'),
        read: item?.scopes?.includes('read'),
        update: item?.scopes?.includes('update'),
        delete: item?.scopes?.includes('delete'),
        process: item?.scopes?.includes('process'),
        download: item?.scopes?.includes('download'),
        verify: item?.scopes?.includes('verify'),
      }));

      // Cache the result
      permissionCache = {
        data: outputArray,
        timestamp: now,
        ttl: 5 * 60 * 1000, // 5 minutes
      };

      sessionStorage.setItem('acl', JSON.stringify(outputArray));

      // Dispatch custom event to notify components of ACL update
      window.dispatchEvent(new CustomEvent('aclUpdated'));

      return outputArray;
    })
    .catch((err) => {
      logger.error('Error fetching permissions:', err);
      // Return cached data if available, even if expired
      if (permissionCache.data) {
        logger.warn('Using cached permissions due to API error');
        return permissionCache.data;
      }
      throw err;
    });
};

// Helper function to check if user has access to a specific menu
export const hasMenuAccess = (menuName) => {
  const menuData = getAclMenu(menuName);
  return menuData && (Array.isArray(menuData) ? menuData.length > 0 : true);
};

// Clear permission cache (useful when user permissions change)
export const clearPermissionCache = () => {
  permissionCache = {
    data: null,
    timestamp: 0,
    ttl: 5 * 60 * 1000,
  };
  sessionStorage.removeItem('acl');
};

// Check if user should be redirected to a specific page after login
export const getRedirectAfterLogin = () => {
  return sessionStorage.getItem('redirectAfterLogin');
};

// Set redirect path after login
export const setRedirectAfterLogin = (path) => {
  if (path && path !== '/login' && path !== '/welcome') {
    sessionStorage.setItem('redirectAfterLogin', path);
  }
};

// Clear redirect path
export const clearRedirectAfterLogin = () => {
  sessionStorage.removeItem('redirectAfterLogin');
};

// Check if user has valid authentication
export const isUserAuthenticated = () => {
  const token = getToken();
  if (!token || token === 'undefined' || token === 'gada') {
    return false;
  }
  const role = getRole();
  if (!role || role === 'norole' || role === 'undefined') {
    return false;
  }
  return true;
};

export const ListMenu = () => {
  const icon = (name) => <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />;
  return [
    {
      title: 'Dashboard',
      path: '/dashboard/app',
      icon: icon('ic_analytics'),
      namaroute: 'dashboard',
    },
    {
      title: 'Dataset Map',
      path: '/dashboard/dataset',
      icon: icon('ic_rule'),
      namaroute: 'data_set',
    },
    {
      title: 'Alerts',
      path: '/dashboard/alert',
      icon: icon('ic_report'),
      namaroute: 'alert',
    },
    {
      title: 'Calendar',
      path: '/dashboard/calendar',
      icon: icon('ic_calendar'),
      namaroute: 'calendar',
    },
    {
      title: 'Information',
      path: '/dashboard/information',
      icon: icon('ic_information'),
      namaroute: 'information',
    },
    {
      title: 'About',
      path: '/dashboard/about',
      icon: icon('ic_information'),
      namaroute: 'about',
    },
    {
      title: 'Reports',
      path: '/dashboard/report',
      icon: icon('ic_rule'),
      namaroute: 'report',
    },
    {
      title: 'Actions',
      path: '/dashboard/action',
      icon: icon('ic_alert'),
      namaroute: 'action',
    },
    {
      title: 'Users',
      path: '/dashboard/user',
      icon: icon('ic_user'),
      namaroute: 'user',
    },
    {
      title: 'Activity Log',
      path: '/dashboard/activitylogs',
      icon: icon('ic_activity'),
      namaroute: 'activity',
    },
    {
      title: 'Logout',
      path: '/login',
      icon: icon('ic_logout'),
      namaroute: 'logout',
    },
  ];
};

// Helper function to get accessible menus based on ACL (same logic as sidebar)
export const getAccessibleMenus = (aclData = null) => {
  const acl = aclData || getAcl();

  // Check if ACL is empty or invalid
  if (!acl || acl === '[]' || acl === 'null' || acl === '' || acl === 'undefined') {
    return [];
  }

  try {
    let aclJson;

    // Handle both string and already parsed JSON
    if (typeof acl === 'string') {
      aclJson = JSON.parse(acl);
    } else {
      aclJson = acl;
    }

    if (!Array.isArray(aclJson) || aclJson.length === 0) {
      return [];
    }

    const allMenus = ListMenu();

    // Filter menus based on ACL read permission (same logic as sidebar in nav/index.js)
    const accessibleMenus = allMenus.filter((c) => {
      // Try to find matching ACL entry by menu route name
      const menu = aclJson?.find((e) => c.namaroute === e.nama_menu && e.read === true);

      // Include menu if it has read permission OR if it's logout (logout is always accessible)
      const isAccessible = menu !== undefined || c.namaroute === 'logout';

      return isAccessible;
    });

    // Exclude logout from redirect (logout is not a page to redirect to)
    const filteredMenus = accessibleMenus.filter((menu) => menu.namaroute !== 'logout');

    return filteredMenus;
  } catch (error) {
    logger.error('Error parsing ACL for accessible menus:', error, { acl });
    return [];
  }
};

// Helper function to get first accessible menu path for redirect
export const getFirstAccessibleMenuPath = (aclData = null) => {
  const accessibleMenus = getAccessibleMenus(aclData);

  if (accessibleMenus.length > 0) {
    return accessibleMenus[0].path;
  }

  return '/dashboard/app'; // Fallback to dashboard
};
